import React from 'react'
import { TableWrap, Segmented } from './components.jsx'
import { fmtCompact, fmtNum } from './format.js'

/* ============================================================
   Chart SVG buatan sendiri — tetap nol dependensi.

   Aturan warna berlapis tiga:
     semantik  >  kategoris (≥2 seri)  >  brand (1 seri netral)

   `--program-color` ditulis ulang saat runtime per lembaga, jadi
   seri kategoris memakai token --cat-* yang hex-nya tetap. Warna
   selalu ditulis sebagai string `var(...)` ke atribut SVG, tidak
   pernah dibekukan lewat getComputedStyle, supaya pergantian
   program me-retema chart secara live.

   Ekspor: useChartSize, useTooltip, niceTicks, seriesColor, seqColor,
           CHART_COLORS, ChartFrame, ChartLegend, ChartTooltip,
           ChartTable, BarChart, GroupedBarChart, DonutChart,
           LineChart, HBarChart, Heatmap
   ============================================================ */

const ACCENT = 'var(--program-color, #B84E2A)'

export const CHART_COLORS = [
  'var(--cat-1)', 'var(--cat-2)', 'var(--cat-3)', 'var(--cat-4)', 'var(--cat-5)',
]

const SEQ_STOPS = 13

const isDev = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV

// Slot kategoris tidak pernah didaur ulang: seri ke-6 bukan hue baru,
// melainkan tanda bahwa data perlu dilipat jadi "Lainnya" atau dipecah
// jadi small multiples.
export function seriesColor(i) {
  if (i < CHART_COLORS.length) return CHART_COLORS[i]
  if (isDev) {
    console.warn(
      `[charts] slot warna kategoris habis di indeks ${i}. ` +
      'Lipat sisanya jadi "Lainnya" atau pakai small multiples — jangan daur ulang warna.'
    )
  }
  return 'var(--text-3)'
}

// Ramp sekuensial satu-hue untuk magnitudo. onDark true mulai stop 8,
// titik di mana teks putih lolos kontras di atas sel.
export function seqColor(v, max = 100) {
  if (v == null || Number.isNaN(v)) return { fill: null, onDark: false }
  const t = Math.max(0, Math.min(1, v / (max || 1)))
  const idx = Math.max(1, Math.min(SEQ_STOPS, Math.round(t * (SEQ_STOPS - 1)) + 1))
  return { fill: `var(--seq-b-${idx})`, onDark: idx >= 8 }
}

// ── useChartSize ─────────────────────────────────────────────
// Lebar terukur, bukan viewBox yang diskalakan: semua stroke, radius,
// dan marker ditulis dalam px asli sehingga tidak ada distorsi, dan
// <text> sumbu tetap berukuran benar. Mengembalikan 0 saat kontainer
// tersembunyi (mis. di dalam tab display:none) — pemanggil menahan
// render alih-alih membagi dengan nol.
export function useChartSize() {
  const ref = React.useRef(null)
  const [width, setWidth] = React.useState(0)

  React.useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const apply = (w) => setWidth((prev) => (Math.abs(prev - w) < 1 ? prev : w))
    apply(el.offsetWidth || 0)
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) apply(Math.round(e.contentRect.width))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return [ref, width]
}

// ── useTooltip ───────────────────────────────────────────────
// Untuk chart yang posisinya hanya bisa diketahui dari DOM (heatmap).
// Chart berbasis SVG memakai `tipFor` di ChartFrame — tooltip di sana
// diturunkan dari state indeks aktif, bukan disimpan terpisah.
export function useTooltip() {
  const [tip, setTip] = React.useState(null)
  const show = React.useCallback((next) => setTip(next), [])
  const hide = React.useCallback(() => setTip(null), [])
  return { tip, show, hide }
}

// ── niceTicks ────────────────────────────────────────────────
// Langkah 1-2-5×10ⁿ. Sumbu nilai selalu menyertakan 0.
export function niceTicks(max, count = 4) {
  const m = max > 0 ? max : 1
  const raw = m / count
  const mag = Math.pow(10, Math.floor(Math.log10(raw)))
  const norm = raw / mag
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * mag
  const niceMax = Math.ceil(m / step) * step
  const ticks = []
  for (let v = 0; v <= niceMax + step * 1e-9; v += step) {
    ticks.push(Number(v.toPrecision(12)))
  }
  return { ticks, niceMax }
}

// Path bar: ujung data membulat, siku di baseline. <rect rx> tidak
// dipakai karena ia membulatkan keempat sudut.
function barPath(x, y, w, h, r = 4) {
  const rr = Math.max(0, Math.min(r, w / 2, h))
  return `M${x},${y + h} L${x},${y + rr} Q${x},${y} ${x + rr},${y} ` +
         `L${x + w - rr},${y} Q${x + w},${y} ${x + w},${y + rr} L${x + w},${y + h} Z`
}

// <text> SVG tidak punya text-overflow, jadi label dipotong di JS dengan
// anggaran karakter dari lebar band terukur. Teks penuh tetap ada di
// <title> dan tooltip.
function truncate(label, budget) {
  const s = String(label ?? '')
  if (budget <= 1 || s.length <= budget) return s
  return `${s.slice(0, Math.max(1, budget - 1))}…`
}

const charBudget = (px, fontSize = 11) => Math.max(2, Math.floor(px / (fontSize * 0.55)))

// Stagger dibatasi 200ms supaya chart dengan banyak bar tidak merayap.
const staggerDelay = (i) => `${Math.min(i * 24, 200)}ms`

const KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Escape']

// Satu roving index untuk seluruh SVG — bukan satu tab stop per bar,
// yang akan menjadikan chart 30 bar sebagai perangkap tab.
function makeKeyNav(count, active, setActive) {
  return (e) => {
    if (!KEYS.includes(e.key)) return
    e.preventDefault()
    if (e.key === 'Escape') { setActive(-1); return }
    const cur = active < 0 ? (e.key === 'ArrowRight' ? -1 : 0) : active
    const next = e.key === 'Home' ? 0
      : e.key === 'End' ? count - 1
      : e.key === 'ArrowLeft' ? Math.max(0, cur - 1)
      : Math.min(count - 1, cur + 1)
    setActive(next)
  }
}

// ── ChartLegend ──────────────────────────────────────────────
// Legend selalu ada untuk ≥2 seri, tidak pernah untuk tepat satu:
// dengan satu warna, judul chart sudah menyebut apa yang diplot.
export function ChartLegend({ items = [] }) {
  if (!items || items.length < 2) return null
  return (
    <div className="chart-legend">
      {items.map((it, i) => (
        <span className="chart-legend-item" key={i}>
          <span className="chart-legend-swatch" style={{ background: it.color }} />
          {it.label}
        </span>
      ))}
    </div>
  )
}

// ── ChartTooltip ─────────────────────────────────────────────
// Nilai memakai font mono, label memakai token teks. Warna seri hanya
// hadir sebagai swatch — teks tidak pernah mengenakan warna data.
export function ChartTooltip({ tip, width }) {
  if (!tip) return null
  const flip = width > 0 && tip.x > width / 2
  const style = { top: Math.max(0, tip.y - 10) }
  if (flip) style.right = Math.max(0, width - tip.x + 12)
  else style.left = tip.x + 12
  return (
    <div className="chart-tooltip" style={style} aria-live="polite">
      {tip.title && <div className="chart-tooltip-title">{tip.title}</div>}
      {tip.rows.map((r, i) => (
        <div className="chart-tooltip-row" key={i}>
          {r.color && <span className="chart-tooltip-swatch" style={{ background: r.color }} />}
          <span className="chart-tooltip-name">{r.name}</span>
          <span className="chart-tooltip-value">{r.value}</span>
        </div>
      ))}
    </div>
  )
}

// ── ChartTable ───────────────────────────────────────────────
// Alternatif teks yang sebenarnya. Selalu ada di DOM (sr-only),
// dipromosikan jadi terlihat oleh toggle.
export function ChartTable({ columns = [], rows = [], caption, srOnly = false }) {
  if (!columns.length) return null
  return (
    <div className={srOnly ? 'sr-only' : undefined}>
      <TableWrap>
        <table className="tbl">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr>{columns.map((c, i) => <th key={i}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {r.map((c, j) => (
                  <td key={j} className={j === 0 ? undefined : 'num'}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
    </div>
  )
}

// ── ChartFrame ───────────────────────────────────────────────
// Cangkang yang dilewati setiap chart SVG: pengukuran, <svg role="img">
// dengan <title>/<desc>, tooltip, legend, tabel sr-only, dan toggle
// tabel opsional.
//
// `children` dan `tipFor` sama-sama menerima { width, height }, jadi
// tooltip diturunkan dari geometri yang sama dengan gambarnya — tidak
// ada state tooltip terpisah yang bisa keluar sinkron.
export function ChartFrame({
  height = 180,
  ariaLabel,
  desc,
  legendItems = [],
  tableColumns,
  tableRows,
  allowTable = false,
  labels = {},
  dataKey,
  tipFor,
  svgProps = {},
  children,
}) {
  const [ref, width] = useChartSize()
  const [mode, setMode] = React.useState('chart')
  const uid = React.useId()
  const titleId = `${uid}-t`
  const descId = `${uid}-d`
  const hasTable = Boolean(tableColumns && tableRows && tableRows.length)

  if (isDev && legendItems.length >= 3 && !hasTable) {
    console.warn(
      `[charts] "${ariaLabel || 'chart'}" punya ${legendItems.length} seri tanpa tableRows. ` +
      'Slot 3 (aqua) dan 4 (kuning) berada di bawah kontras 3:1 — sediakan tampilan tabel.'
    )
  }

  const showTable = allowTable && hasTable && mode === 'table'
  const tip = !showTable && width > 0 && typeof tipFor === 'function'
    ? tipFor({ width, height })
    : null

  return (
    <div className="chart" role="group" aria-label={ariaLabel} ref={ref}>
      {allowTable && hasTable && (
        <div className="chart-toolbar">
          <Segmented
            value={mode}
            onChange={setMode}
            options={[
              { value: 'chart', label: labels.chart || 'Grafik' },
              { value: 'table', label: labels.table || 'Tabel' },
            ]}
          />
        </div>
      )}

      {showTable ? (
        <ChartTable columns={tableColumns} rows={tableRows} caption={ariaLabel} />
      ) : (
        <>
          {width > 0 ? (
            <svg
              key={dataKey}
              className="chart-svg"
              height={height}
              role="img"
              aria-labelledby={`${titleId} ${descId}`}
              {...svgProps}
            >
              <title id={titleId}>{ariaLabel}</title>
              <desc id={descId}>{desc || ariaLabel}</desc>
              {children({ width, height })}
            </svg>
          ) : (
            <div style={{ height }} />
          )}
          {hasTable && (
            <ChartTable columns={tableColumns} rows={tableRows} caption={ariaLabel} srOnly />
          )}
        </>
      )}

      <ChartTooltip tip={tip} width={width} />
      {!showTable && <ChartLegend items={legendItems} />}
    </div>
  )
}

// Sumbu Y + gridline, dipakai bersama oleh bar dan line chart.
function YAxis({ ticks, y, left, right, lang, format }) {
  const f = format || ((v) => fmtCompact(v, lang))
  return ticks.map((tv, i) => (
    <g key={i}>
      <line className="chart-grid-line" x1={left} x2={right} y1={y(tv)} y2={y(tv)} />
      <text className="chart-axis-label" x={left - 8} y={y(tv)} textAnchor="end" dominantBaseline="middle">
        {f(tv)}
      </text>
    </g>
  ))
}

// ── BarChart ─────────────────────────────────────────────────
// Satu seri, bar vertikal. `height` tetap berarti tinggi total komponen
// (label direservasi di dalam) supaya call site lama tidak bergeser.
// data: [{ label, value, color? }]
export function BarChart({
  data = [],
  height = 160,
  max,
  valueSuffix = '',
  accent = ACCENT,
  formatValue,
  lang = 'id',
  ariaLabel,
  desc,
  labels = {},
}) {
  const [active, setActive] = React.useState(-1)
  if (!data.length) return null

  const fmt = formatValue || ((v) => `${fmtNum(v, lang)}${valueSuffix}`)
  const rawMax = max || Math.max(...data.map((d) => d.value), 1)
  const { ticks, niceMax } = niceTicks(rawMax, 4)

  const padL = 38, padR = 8, padT = 12, padB = 22
  const plotH = Math.max(10, height - padT - padB)
  const values = data.map((d) => d.value)

  const geom = (width) => {
    const plotW = Math.max(10, width - padL - padR)
    const band = plotW / data.length
    return {
      plotW,
      band,
      barW: Math.min(24, Math.max(4, band - 8)),
      cx: (i) => padL + i * band + band / 2,
      y: (v) => padT + plotH - (Math.min(v, niceMax) / (niceMax || 1)) * plotH,
    }
  }

  return (
    <ChartFrame
      height={height}
      ariaLabel={ariaLabel || labels.title || 'Bar chart'}
      desc={desc || `${data.length} kategori, dari ${fmt(Math.min(...values))} sampai ${fmt(Math.max(...values))}.`}
      dataKey={`${data.length}:${niceMax}`}
      tableColumns={[labels.category || 'Kategori', labels.value || 'Nilai']}
      tableRows={data.map((d) => [d.label, fmt(d.value)])}
      tipFor={({ width }) => {
        if (active < 0 || active >= data.length) return null
        const g = geom(width)
        const d = data[active]
        return {
          x: g.cx(active),
          y: g.y(d.value),
          title: d.label,
          rows: [{ name: labels.value || 'Nilai', color: d.color || accent, value: fmt(d.value) }],
        }
      }}
      svgProps={{
        tabIndex: 0,
        onKeyDown: makeKeyNav(data.length, active, setActive),
        onBlur: () => setActive(-1),
        onMouseLeave: () => setActive(-1),
      }}
    >
      {({ width }) => {
        const g = geom(width)
        const budget = charBudget(g.band - 4)
        const showValues = data.length <= 8

        return (
          <>
            <YAxis ticks={ticks} y={g.y} left={padL} right={width - padR} lang={lang} />
            {data.map((d, i) => {
              const bx = g.cx(i) - g.barW / 2
              const by = g.y(d.value)
              const bh = Math.max(2, padT + plotH - by)
              return (
                <g key={i}>
                  <path
                    className={`chart-bar${active === i ? ' is-active' : ''}`}
                    d={barPath(bx, by, g.barW, bh)}
                    fill={d.color || accent}
                    style={{ animationDelay: staggerDelay(i) }}
                  />
                  {showValues && (
                    <text className="chart-value-label" x={g.cx(i)} y={by - 6} textAnchor="middle">
                      {fmt(d.value)}
                    </text>
                  )}
                  <text className="chart-cat-label" x={g.cx(i)} y={height - 6} textAnchor="middle">
                    {truncate(d.label, budget)}
                  </text>
                  <rect
                    className="chart-hit"
                    x={padL + i * g.band} y={padT} width={g.band} height={plotH}
                    onMouseEnter={() => setActive(i)}
                  >
                    <title>{`${d.label}: ${fmt(d.value)}`}</title>
                  </rect>
                </g>
              )
            })}
          </>
        )
      }}
    </ChartFrame>
  )
}

// ── GroupedBarChart ──────────────────────────────────────────
// groups: [{ label, values: [n, n, …] }]
// series: [{ name, color? }] — warna default mengikuti slot kategoris.
export function GroupedBarChart({
  groups = [],
  series = [],
  height = 180,
  max,
  valueSuffix = '',
  formatValue,
  lang = 'id',
  ariaLabel,
  desc,
  labels = {},
}) {
  const [active, setActive] = React.useState(-1)
  if (!groups.length || !series.length) return null

  const fmt = formatValue || ((v) => `${fmtNum(v, lang)}${valueSuffix}`)
  const colors = series.map((s, i) => s.color || seriesColor(i))
  const rawMax = max || Math.max(...groups.flatMap((g) => g.values), 1)
  const { ticks, niceMax } = niceTicks(rawMax, 4)

  const padL = 38, padR = 8, padT = 12, padB = 22
  const plotH = Math.max(10, height - padT - padB)

  const geom = (width) => {
    const plotW = Math.max(10, width - padL - padR)
    const band = plotW / groups.length
    const inner = Math.max(6, Math.min(band - 10, series.length * 22))
    return {
      band,
      inner,
      // celah 2px warna permukaan antar bar bersebelahan
      barW: Math.max(3, inner / series.length - 2),
      cx: (i) => padL + i * band + band / 2,
      y: (v) => padT + plotH - (Math.min(v, niceMax) / (niceMax || 1)) * plotH,
    }
  }

  return (
    <ChartFrame
      height={height}
      ariaLabel={ariaLabel || labels.title || 'Grouped bar chart'}
      desc={desc || `${groups.length} kelompok, seri: ${series.map((s) => s.name).join(', ')}.`}
      dataKey={`${groups.length}:${series.length}:${niceMax}`}
      legendItems={series.map((s, i) => ({ label: s.name, color: colors[i] }))}
      tableColumns={[labels.category || 'Kategori', ...series.map((s) => s.name)]}
      tableRows={groups.map((g) => [g.label, ...g.values.map(fmt)])}
      tipFor={({ width }) => {
        if (active < 0 || active >= groups.length) return null
        const g = geom(width)
        const grp = groups[active]
        return {
          x: g.cx(active),
          y: padT + 8,
          title: grp.label,
          rows: series.map((s, si) => ({
            name: s.name, color: colors[si], value: fmt(grp.values[si] ?? 0),
          })),
        }
      }}
      svgProps={{
        tabIndex: 0,
        onKeyDown: makeKeyNav(groups.length, active, setActive),
        onBlur: () => setActive(-1),
        onMouseLeave: () => setActive(-1),
      }}
    >
      {({ width }) => {
        const g = geom(width)
        const budget = charBudget(g.band - 4)
        return (
          <>
            <YAxis ticks={ticks} y={g.y} left={padL} right={width - padR} lang={lang} />
            {groups.map((grp, gi) => {
              const startX = g.cx(gi) - g.inner / 2
              return (
                <g key={gi}>
                  {grp.values.map((v, si) => {
                    const by = g.y(v)
                    return (
                      <path
                        key={si}
                        className={`chart-bar${active === gi ? ' is-active' : ''}`}
                        d={barPath(startX + si * (g.barW + 2), by, g.barW, Math.max(2, padT + plotH - by), 3)}
                        fill={colors[si]}
                        style={{ animationDelay: staggerDelay(gi) }}
                      />
                    )
                  })}
                  <text className="chart-cat-label" x={g.cx(gi)} y={height - 6} textAnchor="middle">
                    {truncate(grp.label, budget)}
                  </text>
                  <rect
                    className="chart-hit"
                    x={padL + gi * g.band} y={padT} width={g.band} height={plotH}
                    onMouseEnter={() => setActive(gi)}
                  >
                    <title>{grp.label}</title>
                  </rect>
                </g>
              )
            })}
          </>
        )
      }}
    </ChartFrame>
  )
}

// ── LineChart ────────────────────────────────────────────────
// series: [{ name, points: [{ x, y }], color? }] — semua seri berbagi
// daftar x yang sama (diambil dari seri pertama).
export function LineChart({
  series = [],
  height = 200,
  area = 'first',
  formatX,
  formatY,
  lang = 'id',
  labels = {},
  allowTable = false,
  ariaLabel,
  desc,
}) {
  const [active, setActive] = React.useState(-1)
  const live = series.filter((s) => s.points && s.points.length)
  if (!live.length) return null

  const xs = live[0].points.map((p) => p.x)
  const n = xs.length
  const fx = formatX || ((v) => String(v))
  const fy = formatY || ((v) => fmtNum(v, lang))
  const colors = live.map((s, i) => s.color || seriesColor(i))
  const rawMax = Math.max(1, ...live.flatMap((s) => s.points.map((p) => p.y)))
  const { ticks, niceMax } = niceTicks(rawMax, 4)

  const padL = 38, padR = 14, padT = 14, padB = 24
  const plotH = Math.max(10, height - padT - padB)

  const geom = (width) => {
    const plotW = Math.max(10, width - padL - padR)
    return {
      plotW,
      X: (i) => (n === 1 ? padL + plotW / 2 : padL + (i / (n - 1)) * plotW),
      Y: (v) => padT + plotH - (Math.min(v, niceMax) / (niceMax || 1)) * plotH,
    }
  }

  return (
    <ChartFrame
      height={height}
      ariaLabel={ariaLabel || labels.title || 'Line chart'}
      desc={desc || `${live.map((s) => s.name).join(', ')} sepanjang ${n} titik waktu; nilai tertinggi ${fy(rawMax)}.`}
      dataKey={`${n}:${live.length}:${niceMax}`}
      legendItems={live.map((s, i) => ({ label: s.name, color: colors[i] }))}
      allowTable={allowTable}
      labels={labels}
      tableColumns={[labels.x || 'Tanggal', ...live.map((s) => s.name)]}
      tableRows={xs.map((x, i) => [fx(x), ...live.map((s) => fy(s.points[i]?.y ?? 0))])}
      tipFor={({ width }) => {
        if (active < 0 || active >= n) return null
        const g = geom(width)
        return {
          x: g.X(active),
          y: padT + 6,
          title: fx(xs[active]),
          rows: live.map((s, si) => ({
            name: s.name, color: colors[si], value: fy(s.points[active]?.y ?? 0),
          })),
        }
      }}
      svgProps={{
        tabIndex: 0,
        onKeyDown: makeKeyNav(n, active, setActive),
        onBlur: () => setActive(-1),
        onMouseLeave: () => setActive(-1),
      }}
    >
      {({ width }) => {
        const { plotW, X, Y } = geom(width)
        const baseline = padT + plotH

        const paths = live.map((s) => {
          const pts = s.points.map((p, i) => `${X(i).toFixed(1)},${Y(p.y).toFixed(1)}`)
          const line = `M${pts.join(' L')}`
          return { line, fill: `${line} L${X(n - 1).toFixed(1)},${baseline} L${X(0).toFixed(1)},${baseline} Z` }
        })

        const tickCount = Math.min(5, n)
        const tickIdx = n <= 1
          ? [0]
          : Array.from({ length: tickCount }, (_, k) => Math.round((k / (tickCount - 1 || 1)) * (n - 1)))

        // Label endpoint hanya dipasang saat seri terpisah cukup jauh di
        // tepi kanan. Ketika mereka menyatu, menggeser label vertikal justru
        // melepasnya dari garisnya — legend + tooltip yang mengambil alih.
        const endYs = live.map((s) => Y(s.points[n - 1]?.y ?? 0))
        const endLabelsFit = endYs.every((a, i) => endYs.every((b, j) => i === j || Math.abs(a - b) >= 14))

        return (
          <>
            <YAxis ticks={ticks} y={Y} left={padL} right={width - padR} lang={lang} />

            {active >= 0 && active < n && (
              <line className="chart-crosshair" x1={X(active)} x2={X(active)} y1={padT} y2={baseline} />
            )}

            {paths.map((p, i) => (
              (area === 'all' || (area === 'first' && i === 0)) ? (
                <path key={`a${i}`} className="chart-area" d={p.fill} fill={colors[i]} fillOpacity={0.1} />
              ) : null
            ))}
            {paths.map((p, i) => (
              <path key={`l${i}`} className="chart-line" d={p.line} stroke={colors[i]} pathLength="1" />
            ))}

            {live.map((s, si) => {
              const cy = Y(s.points[n - 1]?.y ?? 0)
              return (
                <g key={`e${si}`}>
                  <circle className="chart-marker" cx={X(n - 1)} cy={cy} r={4} fill={colors[si]} />
                  {endLabelsFit && (
                    <text className="chart-value-label chart-fade" x={X(n - 1) - 8} y={cy - 9} textAnchor="end">
                      {fy(s.points[n - 1]?.y ?? 0)}
                    </text>
                  )}
                </g>
              )
            })}

            {active >= 0 && active < n && live.map((s, si) => (
              <circle
                key={`m${si}`} className="chart-marker"
                cx={X(active)} cy={Y(s.points[active]?.y ?? 0)} r={4} fill={colors[si]}
              />
            ))}

            {tickIdx.map((i, k) => (
              <text
                key={k} className="chart-axis-label" x={X(i)} y={height - 6}
                textAnchor={k === 0 ? 'start' : k === tickIdx.length - 1 ? 'end' : 'middle'}
              >
                {fx(xs[i])}
              </text>
            ))}

            <rect
              className="chart-hit"
              x={padL} y={padT} width={plotW} height={plotH}
              onMouseMove={(e) => {
                const box = e.currentTarget.getBoundingClientRect()
                const rel = (e.clientX - box.left) / (box.width || 1)
                setActive(Math.max(0, Math.min(n - 1, Math.round(rel * (n - 1)))))
              }}
            />
          </>
        )
      }}
    </ChartFrame>
  )
}

// ── HBarChart ────────────────────────────────────────────────
// Bar horizontal berperingkat. Sengaja div DOM, bukan SVG: labelnya
// judul panjang yang butuh truncation CSS — sesuatu yang tidak dimiliki
// <text> SVG. Satu seri, jadi tanpa legend.
// data: [{ key, label, value, sub?, title? }]
export function HBarChart({
  data = [],
  max,
  valueSuffix = '',
  color = ACCENT,
  formatValue,
  lang = 'id',
  emptyText,
}) {
  if (!data.length) return <div className="chart-empty">{emptyText || '—'}</div>
  const fmt = formatValue || ((v) => `${fmtNum(v, lang)}${valueSuffix}`)
  const m = Math.max(1, max || Math.max(...data.map((d) => d.value)))
  return (
    <div>
      {data.map((d, i) => (
        <div className="chart-hbar" key={d.key ?? i}>
          <div className="chart-hbar-head">
            <span className="chart-hbar-label" title={d.title || d.label}>{d.label}</span>
            <span className="chart-hbar-value">{fmt(d.value)}</span>
          </div>
          <div className="chart-hbar-track">
            <div
              className="chart-hbar-fill"
              style={{
                width: `${Math.max(1, Math.min(100, (d.value / m) * 100))}%`,
                background: color,
                animationDelay: staggerDelay(i),
              }}
            />
          </div>
          {d.sub && <div className="chart-hbar-sub">{d.sub}</div>}
        </div>
      ))}
    </div>
  )
}

// ── Heatmap ──────────────────────────────────────────────────
// <table> sungguhan: semantik, scroll horizontal, dan akses
// screen-reader gratis. Sel dan legend membaca token --seq-b-* yang
// sama, jadi keduanya tidak bisa lagi tidak cocok. Tiap sel mencetak
// angkanya sendiri — pelabelan langsung inilah relief-nya, jadi tidak
// perlu toggle tabel.
// rows: [{ key, label, cells: [n|null] }]
export function Heatmap({
  columns = [],
  rows = [],
  max = 100,
  formatValue,
  lang = 'id',
  rowHeader = 'Program',
  unitLabel,
  minLabel = '0%',
  maxLabel = '100%',
  ariaLabel,
}) {
  const { tip, show, hide } = useTooltip()
  const [ref, width] = useChartSize()
  if (!columns.length || !rows.length) return null
  const fmt = formatValue || ((v) => `${fmtNum(v, lang)}%`)

  return (
    <div className="chart" ref={ref}>
      <TableWrap>
        <table className="heatmap" aria-label={ariaLabel}>
          <thead>
            <tr>
              <th className="heatmap-rowhead" scope="col">{rowHeader}</th>
              {columns.map((c, i) => <th key={i} scope="col">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <th className="heatmap-rowhead" scope="row">{row.label}</th>
                {columns.map((c, j) => {
                  const v = row.cells[j]
                  const { fill, onDark } = seqColor(v, max)
                  return (
                    <td key={j}>
                      <div
                        className={`heatmap-cell${v == null ? ' is-empty' : ''}${onDark ? ' on-dark' : ''}`}
                        style={v == null ? undefined : { background: fill }}
                        onMouseEnter={(e) => {
                          const host = ref.current && ref.current.getBoundingClientRect()
                          if (!host) return
                          const cell = e.currentTarget.getBoundingClientRect()
                          show({
                            x: cell.left - host.left + cell.width / 2,
                            y: cell.top - host.top,
                            title: `${row.label} · ${c}`,
                            rows: [{
                              name: unitLabel || (lang === 'id' ? 'Rata-rata' : 'Average'),
                              color: fill || 'var(--surface-3)',
                              value: v == null ? '—' : fmt(v),
                            }],
                          })
                        }}
                        onMouseLeave={hide}
                      >
                        {v == null ? '—' : fmt(v)}
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
      <div className="heatmap-legend">
        <span className="tiny muted">{minLabel}</span>
        <div className="heatmap-legend-ramp" />
        <span className="tiny muted">{maxLabel}</span>
      </div>
      <ChartTooltip tip={tip} width={width} />
    </div>
  )
}

// ── DonutChart ───────────────────────────────────────────────
// Cincin nilai tunggal (0–100). Track dipatok --border-subtle: track
// bernuansa brand akan terbaca sebagai kategori kedua.
export function DonutChart({ value = 0, size = 132, stroke = 14, color = ACCENT, label, centerText }) {
  const pct = Math.max(0, Math.min(100, value))
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const offset = c - (pct / 100) * c
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <svg
        width={size} height={size} role="img"
        aria-label={`${label ? `${label}: ` : ''}${Math.round(pct)}%`}
        style={{ display: 'block', transform: 'rotate(-90deg)' }}
      >
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border-subtle)" strokeWidth={stroke} />
        <circle
          className="donut-ring"
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={offset}
          strokeLinecap={pct > 0 && pct < 100 ? 'round' : 'butt'}
        />
        <text
          x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
          transform={`rotate(90 ${size / 2} ${size / 2})`}
          style={{ fontSize: size * 0.22, fontWeight: 600, fill: 'var(--text)' }}
        >
          {centerText != null ? centerText : `${Math.round(pct)}%`}
        </text>
      </svg>
      {label && <div style={{ fontSize: 12, color: 'var(--text-3)', textAlign: 'center' }}>{label}</div>}
    </div>
  )
}
